import { graphql, useStaticQuery } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'urql';

import {
  Card,
  CardBody,
  CardCloseButton,
  CardOptions,
  ErrorMessage,
  NakedForm,
  SubmitButton,
  SwitchField,
  useModalContext,
} from '@/components';
import { useTranslate } from '@/contexts';
import { CustomerRestrictionFields } from '@/fragments/CustomerRestriction';
import { useIsMounted } from '@/hooks';
import { Nullable, PlayerId } from '@/types';
import {
  PlayerUpdateRgNetDepositLimitBlockMutation,
  PlayerUpdateRgNetDepositLimitBlockMutationVariables,
} from './__generated__/PlayerUpdateRgNetDepositLimitBlockForm';

const query = graphql`
  query SanityPlayerUpdateRgNetDepositLimitBlockForm {
    sanityPlayerUpdateRgNetDepositLimitBlockForm {
      title {
        ...SanityLocaleString
      }
      description {
        ...SanityLocaleString
      }
      isRgNetDepositLimitBlocked {
        ...SanityLocaleString
      }
      submit {
        ...SanityLocaleString
      }
    }
  }
`;

const updateCustomerRgNetDepositLimitBlockMutation = gql`
  mutation PlayerUpdateRgNetDepositLimitBlock(
    $playerGlobalId: ID!
    $enable: Boolean!
  ) {
    updateCustomerRgNetDepositLimitBlockV2(
      playerGlobalId: $playerGlobalId
      enable: $enable
    ) {
      id
      rgNetDepositLimit {
        ...CustomerRestrictionFields
      }
    }
  }
  ${CustomerRestrictionFields}
`;

const PlayerUpdateRgNetDepositLimitBlockForm: FC<{
  playerGlobalId: PlayerId;
  isBlocked: boolean;
}> = ({ playerGlobalId, isBlocked }) => {
  const staticData =
    useStaticQuery<Queries.SanityPlayerUpdateRgNetDepositLimitBlockFormQuery>(
      query,
    );

  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const isMounted = useIsMounted();
  const form = staticData.sanityPlayerUpdateRgNetDepositLimitBlockForm;
  const { close } = useModalContext();
  const { t } = useTranslate();

  const [updateRgNetDepositLimitBlockState, updateRgNetDepositLimitBlock] =
    useMutation<
      PlayerUpdateRgNetDepositLimitBlockMutation,
      PlayerUpdateRgNetDepositLimitBlockMutationVariables
    >(updateCustomerRgNetDepositLimitBlockMutation);

  const defaultValues = {
    isRgNetDepositLimitBlocked: isBlocked,
  };

  const methods = useForm<typeof defaultValues>({ defaultValues });
  const nextValue = methods.watch('isRgNetDepositLimitBlocked');
  const isUnchanged = nextValue === isBlocked;

  const onSubmit = ({ isRgNetDepositLimitBlocked }: typeof defaultValues) => {
    updateRgNetDepositLimitBlock({
      playerGlobalId,
      enable: isRgNetDepositLimitBlocked,
    }).then((res) => {
      if (res.error?.message && isMounted) {
        setErrorMessage(res.error.message);
      } else if (close) {
        close();
      }
    });
  };

  if (!form) {
    return null;
  }

  return (
    <Card
      size="md"
      title={t(form.title)}
      options={
        <CardOptions>
          <CardCloseButton />
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3">
          <NakedForm
            methods={methods}
            onSubmit={onSubmit}
            className="grid gap-6"
          >
            <div className="space-y-1">
              <div className="block text-gray-600 text-sm dark:text-gray-200">
                {t(form.description)}
              </div>
            </div>
            <SwitchField
              title={t(form.isRgNetDepositLimitBlocked)}
              name="isRgNetDepositLimitBlocked"
              labelPlacement="above"
              className="col-span-full"
              allowUndefinedState={false}
            />
            <ErrorMessage message={errorMessage} />
            <SubmitButton
              value={t(form.submit)}
              disabled={
                updateRgNetDepositLimitBlockState.fetching || isUnchanged
              }
            />
          </NakedForm>
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerUpdateRgNetDepositLimitBlockForm;
